import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSave, FiDownload, FiUpload, FiTrash2, FiEdit2, FiX, FiCheck,
  FiFileText, FiBox, FiLayers, FiDollarSign, FiFolder,
  FiChevronDown, FiAlertTriangle, FiCheckCircle, FiSettings,
  FiRefreshCw
} from 'react-icons/fi';
import { useSolution } from '../../context/SolutionContext';
import { generateEnhancedPDFReport } from '../../utils/enhancedPDFGenerator';
import { generatePDF, generateTextPDF } from '../../utils/pdfGenerator';

// ==================== TOOLTIP COMPONENT ====================
const Tooltip = ({ children, content, position = 'bottom' }) => {
  const [show, setShow] = useState(false);
  
  const positionClasses = {
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <AnimatePresence>
        {show && content && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`absolute ${positionClasses[position]} z-[500] px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap pointer-events-none shadow-lg`}
          >
            {content}
            <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -top-1 left-1/2 -translate-x-1/2"></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== HEADER COMPONENT ====================
const Header = () => {
  const { state, dispatch, saveSolution, loadSolution, deleteSolution, renameSolution, exportSolution, importSolution } = useSolution();
  
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSolutionList, setShowSolutionList] = useState(false);
  const [solutionName, setSolutionName] = useState('');
  const [solutionDescription, setSolutionDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const fileInputRef = useRef(null);

  // ==================== SAVE SOLUTION ====================
  
  const handleSaveSolution = useCallback(() => {
    if (!solutionName.trim()) {
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'error', message: 'Please enter a solution name' } });
      return;
    }
    
    saveSolution(solutionName.trim(), solutionDescription.trim());
    setShowSaveDialog(false);
    setSolutionName('');
    setSolutionDescription('');
    
    dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: `Solution "${solutionName}" saved successfully` } });
  }, [solutionName, solutionDescription, saveSolution, dispatch]);

  // ==================== EXPORT FULL PDF ====================
  
  const handleExportFullPDF = useCallback(async () => {
    setIsGeneratingPDF(true);
    
    try {
      const activeSolution = state.solutions.find(s => s.id === state.activeSolutionId);
      
      const solutionData = {
        name: activeSolution?.name || 'Current Solution',
        description: activeSolution?.description || '',
        components: state.selectedComponents,
        totalCost: state.totalCost,
        validation: state.validation,
        requirements: getRequirementsWithApproaches(state),
        autoArchitectRequirements: state.autoArchitectRequirements,
        approachConstraints: state.approachConstraints,
        selectedApproaches: state.selectedApproaches,
        componentSpecifications: state.componentSpecifications,
        solutionSpecification: state.solutionSpecification,
        transformationData: state.transformationData,
        validationData: state.validationData,
        traceabilityData: state.traceabilityData
      };
      
      const elements = {
        alluvialElement: document.getElementById('alluvial-container') || 
                          document.getElementById('approach-alluvial-container'),
        architectureElement: document.getElementById('architecture-container'),
        dendrographElement: document.getElementById('dendrograph-container')
      };
      
      await generateEnhancedPDFReport(solutionData, elements);
      
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'Complete PDF report generated successfully' } });
    } catch (error) {
      console.error('PDF generation error:', error);
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'error', message: 'Failed to generate PDF: ' + (error.message || 'unknown error') } });
    } finally {
      setIsGeneratingPDF(false);
      setShowExportMenu(false);
    }
  }, [state, dispatch]);

  // ==================== EXPORT TEXT PDF ====================
  
  const handleExportTextPDF = useCallback(() => {
    try {
      const activeSolution = state.solutions.find(s => s.id === state.activeSolutionId);
      const solutionData = {
        name: activeSolution?.name || 'Current Solution',
        components: state.selectedComponents,
        totalCost: state.totalCost,
        validation: state.validation,
        createdAt: new Date().toISOString()
      };
      
      generateTextPDF(solutionData, `${solutionData.name.replace(/\s+/g, '-').toLowerCase()}-text-report.pdf`);
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'Text PDF report generated' } });
    } catch (error) {
      console.error('Text PDF error:', error);
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'error', message: 'Failed to generate text PDF' } });
    }
    setShowExportMenu(false);
  }, [state, dispatch]);

  // ==================== EXPORT ARCHITECTURE PDF ====================
  
  const handleExportArchitecturePDF = useCallback(async () => {
    try {
      const element = document.getElementById('architecture-container') || 
                      document.getElementById('dendrograph-container');
      
      if (element) {
        await generatePDF(element, 'architecture-diagram.pdf');
        dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'Architecture diagram exported' } });
      } else {
        dispatch({ type: 'SHOW_TOAST', payload: { type: 'error', message: 'Architecture diagram not found. Switch to Architecture tab first.' } });
      }
    } catch (error) {
      console.error('Architecture export error:', error);
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'error', message: 'Failed to export architecture' } });
    }
    setShowExportMenu(false);
  }, [dispatch]);

  // ==================== EXPORT JSON ====================
  
  const handleExportJSON = useCallback(() => {
    const activeSolution = state.solutions.find(s => s.id === state.activeSolutionId);
    
    const exportData = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      solution: activeSolution || {
        id: 'current',
        name: 'Current Solution',
        components: state.selectedComponents,
        totalCost: state.totalCost,
        validation: state.validation
      },
      allSolutions: state.solutions,
      autoArchitectRequirements: state.autoArchitectRequirements,
      transformationData: state.transformationData,
      validationData: state.validationData,
      approachConstraints: state.approachConstraints,
      selectedApproaches: state.selectedApproaches,
      componentSpecifications: state.componentSpecifications,
      solutionSpecification: state.solutionSpecification,
      traceabilityData: state.traceabilityData
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(activeSolution?.name || 'solution').replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'Solution exported as JSON' } });
    setShowExportMenu(false);
  }, [state, dispatch]);

  // ==================== IMPORT JSON ====================
  
  const handleImportJSON = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        importSolution(e.target.result);
        dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'Solution imported successfully' } });
      } catch (error) {
        dispatch({ type: 'SHOW_TOAST', payload: { type: 'error', message: 'Failed to import solution' } });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  }, [importSolution, dispatch]);

  // ==================== RENAME ====================
  
  const handleRename = useCallback((id) => {
    if (editingName.trim()) {
      renameSolution(id, editingName.trim());
      setEditingId(null);
      setEditingName('');
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'Solution renamed' } });
    }
  }, [editingName, renameSolution, dispatch]);

  // ==================== DELETE ====================
  
  const handleDelete = useCallback((id, name) => {
    if (window.confirm(`Delete solution "${name}"? This cannot be undone.`)) {
      deleteSolution(id);
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'info', message: `Solution "${name}" deleted` } });
    }
  }, [deleteSolution, dispatch]);

  // ==================== STATS ====================
  
  const activeSolution = state.solutions.find(s => s.id === state.activeSolutionId);
  const componentCount = state.selectedComponents.length;
  const layerCount = new Set(state.selectedComponents.map(c => c.layerId)).size;
  const totalCostLow = state.totalCost?.low?.toFixed?.(0) || '0';
  const totalCostHigh = state.totalCost?.high?.toFixed?.(0) || '0';

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm relative z-30">
      {/* Main Header Row - ICON ONLY with tooltips */}
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between gap-3">
          
          {/* Left: Title (Compact) */}
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white text-base font-bold">AI</span>
            </div>
            <div className="hidden md:block">
              <h1 className="text-base font-bold text-gray-900 leading-tight">
                AI Solution Architect
              </h1>
            </div>
          </div>

          {/* Center: Active Solution Indicator (Compact) */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
            <FiBox className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-700 max-w-[150px] truncate">
              {activeSolution?.name || 'Current Session'}
            </span>
          </div>

          {/* Right: Action Icons Only */}
          <div className="flex items-center gap-1.5">
            
            {/* Save Solution */}
            <Tooltip content="Save Solution" position="bottom">
              <button
                onClick={() => setShowSaveDialog(true)}
                className="p-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all shadow-sm"
              >
                <FiSave className="w-4 h-4" />
              </button>
            </Tooltip>

            {/* Import */}
            <Tooltip content="Import Solution (JSON)" position="bottom">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
              >
                <FiUpload className="w-4 h-4" />
              </button>
            </Tooltip>

            {/* Export Menu */}
            <div className="relative">
              <Tooltip content="Export Options" position="bottom">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={isGeneratingPDF}
                  className="p-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-wait"
                >
                  {isGeneratingPDF ? (
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <FiDownload className="w-4 h-4" />
                  )}
                </button>
              </Tooltip>

              <AnimatePresence>
                {showExportMenu && !isGeneratingPDF && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-[200] overflow-hidden"
                  >
                    <button
                      onClick={handleExportFullPDF}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100"
                    >
                      <FiFileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">Complete PDF Report</div>
                        <div className="text-xs text-gray-500">15 sections: Architecture, BoM, Specs, Cost</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleExportTextPDF}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 transition-colors text-left border-b border-gray-100"
                    >
                      <FiFileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">Text PDF Report</div>
                        <div className="text-xs text-gray-500">Simple component list</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleExportArchitecturePDF}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left border-b border-gray-100"
                    >
                      <FiBox className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">Architecture Diagram</div>
                        <div className="text-xs text-gray-500">Export current architecture view</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleExportJSON}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors text-left"
                    >
                      <FiDownload className="w-5 h-5 text-orange-600 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">Export JSON</div>
                        <div className="text-xs text-gray-500">Full solution data</div>
                      </div>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Solutions List */}
            {state.solutions.length > 0 && (
              <Tooltip content={`${state.solutions.length} Saved Solutions`} position="bottom">
                <button
                  onClick={() => setShowSolutionList(!showSolutionList)}
                  className="p-2.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all relative"
                >
                  <FiFolder className="w-4 h-4" />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {state.solutions.length}
                  </span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Stats Bar - Compact with Icons */}
        <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
          <Tooltip content="Total Components" position="bottom">
            <span className="flex items-center gap-1.5 text-gray-600 cursor-help">
              <FiBox className="w-3.5 h-3.5 text-blue-500" />
              <strong>{componentCount}</strong>
            </span>
          </Tooltip>
          
          <Tooltip content="Layers Covered" position="bottom">
            <span className="flex items-center gap-1.5 text-gray-600 cursor-help">
              <FiLayers className="w-3.5 h-3.5 text-purple-500" />
              <strong>{layerCount}</strong>
            </span>
          </Tooltip>
          
          <Tooltip content="Estimated Cost Range" position="bottom">
            <span className="flex items-center gap-1.5 text-gray-600 cursor-help">
              <FiDollarSign className="w-3.5 h-3.5 text-green-500" />
              <strong>${totalCostLow} - ${totalCostHigh}</strong>
            </span>
          </Tooltip>
          
          {state.validation?.isValid ? (
            <Tooltip content="Solution Valid" position="bottom">
              <span className="flex items-center gap-1.5 text-green-600 cursor-help">
                <FiCheckCircle className="w-3.5 h-3.5" />
                <strong>Valid</strong>
              </span>
            </Tooltip>
          ) : (
            <Tooltip content={`${state.validation?.issues?.length || 0} Issues Found`} position="bottom">
              <span className="flex items-center gap-1.5 text-red-600 cursor-help">
                <FiAlertTriangle className="w-3.5 h-3.5" />
                <strong>{state.validation?.issues?.length || 0}</strong>
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* ==================== SAVED SOLUTIONS DROPDOWN ==================== */}
      <AnimatePresence>
        {showSolutionList && state.solutions.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-200 bg-gray-50 overflow-hidden"
          >
            <div className="p-3 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">
                  Saved Solutions ({state.solutions.length})
                </span>
                <button onClick={() => setShowSolutionList(false)} className="p-1 hover:bg-gray-200 rounded">
                  <FiX className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-2">
                {state.solutions.map(solution => (
                  <div
                    key={solution.id}
                    className={`flex items-center gap-3 p-3 bg-white rounded-lg border ${
                      state.activeSolutionId === solution.id 
                        ? 'border-blue-400 ring-2 ring-blue-200' 
                        : 'border-gray-200'
                    }`}
                  >
                    <FiFolder className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    
                    <div className="flex-1 min-w-0">
                      {editingId === solution.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(solution.id);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                          />
                          <button onClick={() => handleRename(solution.id)} className="p-1 hover:bg-green-100 rounded">
                            <FiCheck className="w-3.5 h-3.5 text-green-600" />
                          </button>
                          <button onClick={() => setEditingId(null)} className="p-1 hover:bg-red-100 rounded">
                            <FiX className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { loadSolution(solution.id); setShowSolutionList(false); }}
                          className="w-full text-left"
                        >
                          <div className="font-semibold text-gray-900 text-sm truncate">{solution.name}</div>
                          <div className="text-xs text-gray-500">
                            {solution.components?.length || 0} comps • {new Date(solution.updatedAt).toLocaleDateString()}
                          </div>
                        </button>
                      )}
                    </div>
                    
                    {editingId !== solution.id && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingId(solution.id); setEditingName(solution.name); }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Rename"
                        >
                          <FiEdit2 className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button
                          onClick={() => exportSolution(solution.id)}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Export JSON"
                        >
                          <FiDownload className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(solution.id, solution.name)}
                          className="p-1.5 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <FiTrash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== SAVE DIALOG ==================== */}
      <AnimatePresence>
        {showSaveDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]"
            onClick={() => setShowSaveDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FiSave className="w-5 h-5 text-blue-600" />
                Save Solution Variant
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Solution Name *
                </label>
                <input
                  type="text"
                  value={solutionName}
                  onChange={(e) => setSolutionName(e.target.value)}
                  placeholder="e.g., Healthcare-AI-Production-v1"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={solutionDescription}
                  onChange={(e) => setSolutionDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleSaveSolution}
                  className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition-colors"
                >
                  Save Solution
                </button>
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImportJSON}
        className="hidden"
      />
    </header>
  );
};

// ==================== HELPER: Get Requirements with Approaches ====================

function getRequirementsWithApproaches(state) {
  const components = state.selectedComponents || [];
  const constraints = state.approachConstraints || {};
  
  const approachNames = {
    1: 'Build In-House', 2: 'Buy/Subscribe SaaS', 3: 'Partner with Vendors',
    4: 'Embed AI via APIs', 5: 'Acquire AI Companies', 6: 'AI Ecosystem Venture',
    7: 'Open Source Adoption', 8: 'Hybrid Approach', 9: 'AI-as-a-Service',
    10: 'Industry Consortium', 11: 'AI Sovereignty', 12: 'Edge AI',
    13: 'Federated Learning', 14: 'Agentic AI', 15: 'Neuro-Symbolic AI',
    16: 'Quantum-Enhanced AI', 17: 'Human-in-the-Loop', 18: 'AI Marketplaces',
    19: 'Regulatory Sandbox', 20: 'Circular AI', 21: 'AI-as-Commodity',
    22: 'Embedded AI in Hardware', 23: 'Biologically-Inspired AI', 24: 'Zero-Knowledge AI',
    25: 'AI Swarm Systems', 26: 'Synthetic Data Generation', 27: 'Causal AI',
    28: 'Multimodal AI', 29: 'Continuous Learning', 30: 'AI Governance & Alignment',
    31: 'Emotion AI', 32: 'Physical AI', 33: 'Decentralized AI',
    34: 'AI for Scientific Discovery', 35: 'Humanoid Robotics', 36: 'Digital Twin',
    37: 'Conversational AI', 38: 'AI-Augmented Reality', 39: 'Self-Healing IT Operations',
    40: 'Post-LLM Paradigms'
  };
  
  const reqCategories = [
    { id: 'business-value', name: 'Business Value Generation', category: 'business', priority: 'Critical' },
    { id: 'data-processing', name: 'Data Processing Capability', category: 'functional', priority: 'High' },
    { id: 'model-inference', name: 'Model Inference', category: 'functional', priority: 'High' },
    { id: 'scalable-infrastructure', name: 'Scalable Infrastructure', category: 'technical', priority: 'High' },
    { id: 'data-storage', name: 'Data Storage & Management', category: 'data', priority: 'High' },
    { id: 'data-protection', name: 'Data Protection & Security', category: 'security', priority: 'Critical' },
    { id: 'real-time-processing', name: 'Real-time Processing', category: 'performance', priority: 'High' },
    { id: 'regulatory-compliance', name: 'Regulatory Compliance', category: 'compliance', priority: 'Critical' },
    { id: 'monitoring-observability', name: 'Monitoring & Observability', category: 'operational', priority: 'High' }
  ];
  
  return reqCategories.map(req => {
    let approachId = 8;
    
    if (constraints.budget === 'low') {
      const mapping = {
        'business-value': 2, 'data-processing': 7, 'model-inference': 21,
        'scalable-infrastructure': 9, 'data-storage': 7, 'data-protection': 30,
        'real-time-processing': 21, 'regulatory-compliance': 30, 'monitoring-observability': 9
      };
      approachId = mapping[req.id] || 2;
    } else if (constraints.budget === 'high') {
      const mapping = {
        'business-value': 1, 'data-processing': 1, 'model-inference': 12,
        'scalable-infrastructure': 1, 'data-storage': 1, 'data-protection': 24,
        'real-time-processing': 22, 'regulatory-compliance': 30, 'monitoring-observability': 39
      };
      approachId = mapping[req.id] || 1;
    } else {
      const mapping = {
        'business-value': 8, 'data-processing': 7, 'model-inference': 12,
        'scalable-infrastructure': 1, 'data-storage': 7, 'data-protection': 30,
        'real-time-processing': 12, 'regulatory-compliance': 30, 'monitoring-observability': 39
      };
      approachId = mapping[req.id] || 8;
    }
    
    return {
      ...req,
      approachId,
      approachName: approachNames[approachId] || 'Default Approach',
      status: components.length > 0 ? 'satisfied' : 'partial'
    };
  });
}

export default Header;